import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Honorific titles to skip when deriving a greeting from a full customer name.
// Mirrors backend/comms.py::_HONORIFIC_TITLES — keep in sync.
const HONORIFIC_TITLES = new Set([
  "mr", "mrs", "ms", "miss", "mx", "mister",
  "dr", "doctor", "prof", "professor",
  "sir", "dame", "lord", "lady", "rev", "reverend",
  "fr", "father", "sr", "br", "brother", "sister",
  "capt", "captain", "col", "colonel", "lt", "lieutenant",
  "sgt", "sergeant", "maj", "major", "gen", "general",
  "hon", "honourable",
]);

/**
 * Return a greeting-safe display name from a free-form customer name.
 *   "Mr Chris Obi"  → "Chris"
 *   "Mrs J Smith"   → "Mrs Smith"
 *   "OBI, CHRIS"    → "Chris"
 *   ""              → fallback ("there")
 */
export function firstName(fullName, fallback = "there") {
  if (!fullName || typeof fullName !== "string") return fallback;
  let name = fullName.trim().replace(/^[-,.\s]+|[-,.\s]+$/g, "");
  if (!name) return fallback;

  // "SURNAME, FIRSTNAME" DMS exports — flip them.
  if (name.includes(",")) {
    const [head, ...rest] = name.split(",");
    const tail = rest.join(",").trim();
    if (tail) name = `${tail} ${head.trim()}`;
  }

  const rawTokens = name.replace(/\./g, " ").split(/\s+/).filter(Boolean);
  const norm = (tok) => {
    const clean = tok.replace(/^[-,.'`]+|[-,.'`]+$/g, "").slice(0, 30);
    if (!clean) return "";
    if (clean === clean.toLowerCase() || clean === clean.toUpperCase()) {
      return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
    }
    return clean;
  };

  let titleToken = "";
  const rest = [];
  for (const tok of rawTokens) {
    const lower = tok.toLowerCase().replace(/^[-,.'`]+|[-,.'`]+$/g, "");
    if (!lower) continue;
    if (HONORIFIC_TITLES.has(lower) && !titleToken) {
      titleToken = norm(tok);
      continue;
    }
    rest.push(tok);
  }

  const multi = rest.filter((t) => t.replace(/^[-,.'`]+|[-,.'`]+$/g, "").length > 1);

  if (multi.length >= 2) return norm(multi[0]);
  if (multi.length === 1) {
    return titleToken ? `${titleToken} ${norm(multi[0])}` : norm(multi[0]);
  }
  return fallback;
}
